import React, { useMemo, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useRestaurant } from '../hooks/useRestaurant';
import { useRestaurantMenus } from '../hooks/useRestaurantMenus';
import { useCart } from '../contexts/CartContext';
import BackHeader from '../components/BackHeader';
import RestaurantDetailCard from '../components/RestaurantDetailCard';
import NavigationBar from '../components/NavigationBar';
import MenuSection from '../components/MenuSection';
import CartBottomSheet from '../components/CartBottomSheet';
import LoadingState from '../components/LoadingState';
import ErrorState from '../components/ErrorState';
import EmptyState from '../components/EmptyState';
import { getStatusMessages } from '../config/constants';
import useTranslation from '../hooks/useTranslation';
import { getCurrencyFromLanguage } from '../utils/currencyUtils';
import { getCurrentLanguage } from '../config/languageConfig';

const RestaurantDetail: React.FC = () => {
  const { restaurantId = '' } = useParams<{ restaurantId: string }>();
  const navigate = useNavigate();
  const { setRestaurantMinOrderAmount } = useCart();
  const { t } = useTranslation();
  
  // 현재 언어에 맞는 통화 설정
  const currentLang = getCurrentLanguage();
  const selectedCurrency = getCurrencyFromLanguage(currentLang);
  
  // 다국어 상태 메시지 가져오기
  const STATUS_MESSAGES = getStatusMessages();
  
  // 가게 정보 가져오기
  const { restaurant, loading: restaurantLoading, error: restaurantError } = useRestaurant(restaurantId);
  
  // 메뉴 정보 가져오기
  const { menuSections, loading: menuLoading, error: menuError, refetch: refetchMenus } = useRestaurantMenus(restaurantId);
  
  // 최소 주문 금액 - 현재 언어에 맞는 통화의 최소 주문 금액 사용
  const minOrderAmount = useMemo(() => {
    if (!restaurant) return null;
    
    // 현재 언어에 맞는 통화 결정
    const currency = getCurrencyFromLanguage(currentLang);
    
    // 통화에 맞는 필드 이름 구성 (예: minOrderAmountKrw, minOrderAmountUsd 등)
    const currencyCode = currency.charAt(0).toUpperCase() + currency.slice(1).toLowerCase();
    const minOrderAmountField = `minOrderAmount${currencyCode}`;
    
    // 언어에 맞는 최소 주문금액이 있으면 사용, 없으면 기본 최소 주문금액 사용
    return restaurant[minOrderAmountField as keyof typeof restaurant] as number || restaurant.minOrderAmount;
  }, [restaurant, currentLang]);
  
  // CartContext에 최소 주문 금액 저장
  useEffect(() => {
    if (minOrderAmount !== null && restaurant) {
      // 서버 응답에서 각 통화별 최소 주문 금액 가져오기
      const minOrderAmounts = {
        krw: restaurant.minOrderAmountKrw || restaurant.minOrderAmount || 0,
        usd: restaurant.minOrderAmountUsd || restaurant.minOrderAmount || 0,
        jpy: restaurant.minOrderAmountJpy || restaurant.minOrderAmount || 0,
        cny: restaurant.minOrderAmountCny || restaurant.minOrderAmount || 0,
        twd: restaurant.minOrderAmountTwd || restaurant.minOrderAmount || 0,
      };
      
      setRestaurantMinOrderAmount(minOrderAmounts);
    }
  }, [minOrderAmount, restaurant, setRestaurantMinOrderAmount]);
  
  // 현재 언어에 맞는 레스토랑 이름 가져오기
  const displayName = useMemo(() => {
    if (restaurantLoading || !restaurant) return '[Shop Name]';
    
    // 현재 언어에 맞는 이름 확인
    switch(currentLang) {
      case 'ko':
        if (restaurant.nameKo) return restaurant.nameKo;
        break;
      case 'ja':
        if (restaurant.nameJa) return restaurant.nameJa;
        break;
      case 'zh-CN':
        if (restaurant.nameZhCn) return restaurant.nameZhCn;
        break;
      case 'zh-TW':
        if (restaurant.nameZhTw) return restaurant.nameZhTw;
        break;
      // 'en'은 기본 fallback이므로 아래에서 처리
    }
    
    // 현재 언어가 영어가 아니고, 영어 이름을 사용할 경우 영어 표시 추가
    if (restaurant.nameEn) {
      if (currentLang !== 'en') {
        return `${restaurant.nameEn}`;
      }
      return restaurant.nameEn;
    }
    
    // 영문도 없으면 기본 이름 사용
    return restaurant.name;
  }, [restaurant, restaurantLoading, currentLang]);
  
  // 장바구니 페이지로 이동
  const handleCheckout = () => {
    navigate('/cart', { replace: false });
  };
  
  // restaurantId가 없는 경우
  if (!restaurantId) {
    return (
      <div className="min-h-screen bg-gray-50">
        <BackHeader title={t('error.title')} />
        <div className="p-4">
          <ErrorState message={t('restaurant.invalidId')} onRetry={() => window.location.reload()} />
        </div>
        <NavigationBar />
      </div>
    );
  }
  
  // 로딩 중 표시
  if (restaurantLoading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <BackHeader title={displayName} />
        <div className="p-4">
          <LoadingState 
            message={STATUS_MESSAGES.loading.restaurants} 
            id={`restaurant-detail-${restaurantId}`}
          />
        </div>
        <NavigationBar />
      </div>
    );
  }
  
  // 에러 표시
  if (restaurantError) {
    return (
      <div className="min-h-screen bg-gray-50">
        <BackHeader title={t('error.title')} />
        <div className="p-4">
          <ErrorState message={restaurantError} onRetry={() => window.location.reload()} />
        </div>
        <NavigationBar />
      </div>
    );
  }
  
  // 가게 정보가 없을 때
  if (!restaurant) {
    return (
      <div className="min-h-screen bg-gray-50">
        <BackHeader title={t('error.notFound')} />
        <div className="p-4">
          <EmptyState message={t('restaurant.notFound')} />
        </div>
        <NavigationBar />
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-gray-50">
      {/* GNB */}
      <BackHeader title={displayName} />
      
      <main className="p-4 pb-24">
        {/* 가게 정보 카드 */}
        <div className="mb-4">
          <RestaurantDetailCard 
            restaurant={restaurant} 
            currency={selectedCurrency}
            isFromCategory={false}
          />
        </div>
        
        {/* 구분선 */}
        <div className="border-b border-gray-200 my-4"></div>
        
        {/* 메뉴 섹션 */}
        {menuLoading ? (
          <LoadingState 
            message={STATUS_MESSAGES.loading.menus} 
            id={`restaurant-menus-${restaurantId}`}
          />
        ) : menuError ? (
          <ErrorState message={menuError} onRetry={refetchMenus} />
        ) : menuSections.length === 0 ? (
          <EmptyState 
            message={STATUS_MESSAGES.empty.menus} 
            subtitle={t('restaurant.noMenuItems')}
            actionText={t('action.refresh')}
            onAction={refetchMenus}
            hideIcon={true}
          />
        ) : (
          <div className="space-y-2">
            {menuSections.map(section => (
              <MenuSection 
                key={section.id} 
                section={section} 
                restaurantId={restaurantId}
                restaurantName={displayName}
                restaurantMultiNames={{
                  name: restaurant.name,
                  nameEn: restaurant.nameEn || restaurant.name,
                  nameKo: restaurant.nameKo || restaurant.nameEn || restaurant.name,
                  nameJa: restaurant.nameJa || restaurant.nameEn || restaurant.name,
                  nameZhCn: restaurant.nameZhCn || restaurant.nameEn || restaurant.name,
                  nameZhTw: restaurant.nameZhTw || restaurant.nameEn || restaurant.name
                }}
                currency={selectedCurrency}
              />
            ))}
            {/* 바텀 시트와의 간격을 확보하기 위한 여백 */}
            <div className="h-20"></div>
          </div>
        )}
      </main>
      
      {/* 장바구니 바텀시트 */}
      <CartBottomSheet 
        minOrderAmount={minOrderAmount}
        onCheckout={handleCheckout}
        restaurantId={restaurantId}
        currency={selectedCurrency}
      />
      
      {/* 하단 네비게이션 바 */}
      <NavigationBar />
    </div>
  );
};

export default RestaurantDetail; 